import React from 'react';
import { Box, Typography, Breadcrumbs, Link, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { NavigateNext as NavigateNextIcon, Add as AddIcon } from '@mui/icons-material';
import { BaseButton } from './BaseButton';
import { BreadcrumbItem } from '../../../types/admin.types';

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
    variant?: 'contained' | 'outlined' | 'text';
    colorVariant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  }[];
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs = [],
  actions = [],
}) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 4 }}>
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((item, index) => (
            <React.Fragment key={index}>
              {item.path ? (
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate(item.path!)}
                  sx={{ cursor: 'pointer', color: '#005e5d', fontWeight: 500, '&:hover': { color: '#004d4c' } }}
                >
                  {item.label}
                </Link>
              ) : (
                <Typography variant="body2" sx={{ color: '#999999' }}>
                  {item.label}
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#333333' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: '#999999', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {actions.length > 0 && (
          <Stack direction="row" spacing={1}>
            {actions.map((action, index) => (
              <BaseButton
                key={index}
                variant={action.variant || 'contained'}
                colorVariant={action.colorVariant || 'primary'}
                startIcon={action.icon || (index === 0 ? <AddIcon /> : undefined)}
                onClick={action.onClick}
              >
                {action.label}
              </BaseButton>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
